import { io } from "socket.io-client"
import get from "lodash/get"
import authService from "./auth/auth.service"
import { getAgentHeaders } from "./agentWrites"
import { getDemoTourHeaders, handleDemoTourError } from "./demoTour/demoTourFlag"
import { getFileMetadata } from "./file/file.service"
import { FILE_UPLOAD_SIZE_LIMIT_MB, FILE_UPLOAD_LIMIT } from "./file/file.const"
import { convertSize } from "../comp/Utils"

import axios from "axios"
const API_HOST = process.env.REACT_APP_API_SERVICE_HOST
const AI_HOST = process.env.REACT_APP_AI_SERVICE_HOST

// Firebase decides a token is still valid using the device clock, so a machine
// running behind Google's clock keeps sending tokens the server already treats
// as expired. Recovery is to force a refresh once the server says so.
// A deploy drops every socket at once, so many callers can hit this in the same
// tick across both Api instances - share one in-flight refresh between them.
let pendingForceRefresh = null

function forceRefreshUserToken() {
    if (!pendingForceRefresh) {
        pendingForceRefresh = authService
            .getCurrentUser()
            .then((user) => user.getIdToken(true))
            .catch(() => null)
            .then((token) => {
                pendingForceRefresh = null
                return token
            })
    }

    return pendingForceRefresh
}

class Api {
    constructor(host) {
        this.host = host

        this.public = {
            get: (url) => this.get(url, true),
            post: (url, payload) => this.post(url, payload, true),
            put: (url, payload) => this.post(url, payload, true),
            delete: (url) => this.get(url, true),
        }

        this.socket = {
            connect: (namespace, options) => this.connect(namespace, options),
        }
    }

    /**
     *
     * @param url
     * @param isPublic
     * @param options
     * @return {Promise<string>}
     */
    get(url, isPublic = false, options = {}) {
        if (typeof isPublic === "object") {
            options = isPublic
            isPublic = false
        }

        return this._getUserToken(isPublic).then((token) => {
            const reqOptions = {
                method: "get",
                url: `${this.host}${url}`,
                ...options,
            }

            if (token) {
                reqOptions.headers = {
                    Authorization: `Bearer ${token}`,
                    "user-request-id": authService.getUserRequestId(),
                    ...getAgentHeaders(),
                    ...getDemoTourHeaders(),
                    ...options.headers,
                }
            }

            return this._requestWithTokenRetry(reqOptions, isPublic)
        })
    }

    /**
     * @param url
     * @param isPublic
     * @param options
     * @return {Promise<string>}
     */
    fetch(url, isPublic = false, options = {}) {
        if (typeof isPublic === "object") {
            options = isPublic
            isPublic = false
        }

        return this._getUserToken(isPublic).then((token) => {
            const reqOptions = {
                method: options.method || "get",
                ...options,
            }

            if (token) {
                reqOptions.headers = {
                    Authorization: `Bearer ${token}`,
                    "user-request-id": authService.getUserRequestId(),
                    ...getAgentHeaders(),
                    ...getDemoTourHeaders(),
                    ...options.headers,
                }
            }

            return fetch(`${this.host}${url}`, reqOptions)
        })
    }

    post(url, payload, isPublic = false, options = {}) {
        if (typeof isPublic === "object") {
            options = isPublic
            isPublic = false
        }

        return this._getUserToken(isPublic).then((token) => {
            const reqOptions = {
                method: "post",
                url: `${this.host}${url}`,
                data: payload,
                ...options,
            }

            if (token) {
                reqOptions.headers = {
                    Authorization: `Bearer ${token}`,
                    "user-request-id": authService.getUserRequestId(),
                    ...getAgentHeaders(),
                    ...getDemoTourHeaders(),
                    ...options.headers,
                }
            }

            return this._requestWithTokenRetry(reqOptions, isPublic)
        })
    }

    put(url, payload, isPublic = false, options = {}) {
        if (typeof isPublic === "object") {
            options = isPublic
            isPublic = false
        }

        return this._getUserToken(isPublic).then((token) => {
            const reqOptions = {
                method: "put",
                url: `${this.host}${url}`,
                data: payload,
                ...options,
            }

            if (token) {
                reqOptions.headers = {
                    Authorization: `Bearer ${token}`,
                    "user-request-id": authService.getUserRequestId(),
                    ...getAgentHeaders(),
                    ...getDemoTourHeaders(),
                    ...options.headers,
                }
            }

            return this._requestWithTokenRetry(reqOptions, isPublic)
        })
    }

    patch(url, payload, isPublic = false, options = {}) {
        if (typeof isPublic === "object") {
            options = isPublic
            isPublic = false
        }

        return this._getUserToken(isPublic).then((token) => {
            const reqOptions = {
                method: "patch",
                url: `${this.host}${url}`,
                data: payload,
                ...options,
            }

            if (token) {
                reqOptions.headers = {
                    Authorization: `Bearer ${token}`,
                    "user-request-id": authService.getUserRequestId(),
                    ...getAgentHeaders(),
                    ...getDemoTourHeaders(),
                    ...options.headers,
                }
            }

            return this._requestWithTokenRetry(reqOptions, isPublic)
        })
    }

    delete(url, isPublic = false, options = {}) {
        if (typeof isPublic === "object") {
            options = isPublic
            isPublic = false
        }

        return this._getUserToken(isPublic).then((token) => {
            const reqOptions = {
                method: "delete",
                url: `${this.host}${url}`,
                ...options,
            }

            if (token) {
                reqOptions.headers = {
                    Authorization: `Bearer ${token}`,
                    "user-request-id": authService.getUserRequestId(),
                    ...getAgentHeaders(),
                    ...getDemoTourHeaders(),
                    ...options.headers,
                }
            }

            return this._requestWithTokenRetry(reqOptions, isPublic)
        })
    }

    async uploadFileSingle(url, file, options = {}) {
        const formData = new FormData()
        formData.append(options.name || "file", file)

        if (convertSize(file.size, "bytes", "MB") >= FILE_UPLOAD_SIZE_LIMIT_MB) {
            throw this.getResponseErrorFileUpload({
                code: "LIMIT_FILE_SIZE",
            })
        }

        return this.post(url, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
            onUploadProgress: (progressEvent) => {
                if (typeof options.onUploadProgress === "function") {
                    options.onUploadProgress(progressEvent.loaded, progressEvent.total)
                }
            },
        }).catch((err) => {
            throw this.getResponseErrorFileUpload(err)
        })
    }

    async uploadFileMulti(url, files, options = {}) {
        const formData = new FormData()

        if (!Array.isArray(files)) {
            files = [files]
        }

        if (files.length > FILE_UPLOAD_LIMIT) {
            throw this.getResponseErrorFileUpload({
                code: "LIMIT_FILE",
            })
        }

        if (files.some((item) => convertSize(item.size, "bytes", "MB") >= FILE_UPLOAD_SIZE_LIMIT_MB)) {
            throw this.getResponseErrorFileUpload({
                code: "LIMIT_FILE_SIZE",
            })
        }

        try {
            // Set metadata
            let metadata = {}
            for (let file of files) {
                metadata[file.name] = await getFileMetadata(file)
            }
            formData.append("metadata", JSON.stringify(metadata))
        } catch (err) {
            // Do nothing
        }

        ;(files || []).forEach((item) => formData.append("file", item))

        // Plain text fields that travel with the upload. Multer puts them on req.body,
        // so the route has to parse them with `.any()` (or a matching field spec).
        Object.entries(options.fields || {}).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                formData.append(key, value)
            }
        })

        return !formData.entries().next().done
            ? this.post(url, formData, {
                  headers: {
                      "Content-Type": "multipart/form-data",
                  },
                  onUploadProgress: (progressEvent) => {
                      if (typeof options.onUploadProgress === "function") {
                          options.onUploadProgress(progressEvent.loaded, progressEvent.total)
                      }
                  },
              }).catch((err) => {
                  throw this.getResponseErrorFileUpload(err)
              })
            : {
                  data: [],
              }
    }

    connect(namespace, options = {}) {
        const host = this.host

        const socket = io(`${host}/${namespace.startsWith("/") ? namespace.slice(1) : namespace}`, {
            ...options,
            query: {
                ...options.query,
                userRequestId: authService.getUserRequestId(),
            },
            // socket.io sends the same auth on every reconnect, so a token fixed at creation
            // had expired by the time Cloud Run cut the connection (60 min): every reconnect
            // was refused before it refreshed. As a callback it reads the current token each time.
            auth: (cb) => {
                this._getUserToken().then((token) => cb({ ...options.auth, token }))
            },
            withCredentials: true,
            transports: ["websocket"],
        })

        let hasRetriedAuth = false

        return new Promise((resolve, reject) => {
            socket.on("connect", () => {
                hasRetriedAuth = false
                resolve(socket)
            })
            socket.on("connect_error", (err) => {
                // socket.io v4 delivers an Error, so the server's code arrives on
                // err.message. Comparing against the raw payload (the v2 shape) never
                // matched, which left the socket dead after its token aged out.
                if (err?.message !== "auth/id-token-expired" || hasRetriedAuth) {
                    return
                }

                // One attempt per outage: the refreshed token is genuinely new, so a
                // second rejection means retrying again would only spin.
                hasRetriedAuth = true

                forceRefreshUserToken().then((token) => {
                    if (!token) {
                        return
                    }

                    // The auth callback reads the refreshed token
                    socket.connect()
                })
            })
        })
    }

    getCancelToken() {
        return axios.CancelToken
    }

    isCancel(err) {
        return axios.isCancel(err)
    }

    getResponseError(err) {
        return get(err || {}, "response.data") || err
    }

    getResponseErrorFileUpload(err) {
        const error = get(err, "response.data") || err
        const errorStatus = err.status || error.status
        const errorCode = err.code || error.code
        let message

        switch (errorStatus) {
            case 413:
                message = `Total file size too large to upload. Make sure your internet connection allows it.`
                break
            case 502:
                message = `Failed to upload the files. If this issue persists please contact us. `
                break
            default:
        }

        if (errorCode === "LIMIT_FILE_SIZE") {
            message = `Individual file limit of ${FILE_UPLOAD_SIZE_LIMIT_MB}MB exceeded.`
        }

        if (errorCode === "LIMIT_FILE") {
            message = `Upload limit of ${FILE_UPLOAD_LIMIT} files at a time exceeded.`
        }

        return {
            ...error,
            message: message || error.message,
        }
    }

    _getUserToken(isPublic) {
        if (isPublic === true) {
            return Promise.resolve()
        }

        return authService
            .getCurrentUser()
            .then((user) => user.getIdToken())
            .catch(() => {})
    }

    _isExpiredTokenError(err) {
        return err?.response?.status === 401 && get(err, "response.data.code") === "auth/id-token-expired"
    }

    /**
     * fetch() with the same one-shot expired-token replay as _requestWithTokenRetry.
     *
     * api.fetch has no 401 recovery of its own, which is fine for a blob download but
     * not for a long-lived stream (mAX SSE), where an aged-out token kills the turn
     * before a single frame arrives. The body of an ok response is never touched, so
     * the caller still owns the stream; the 401 body is read from a clone so the
     * original response stays readable when the error is not an expired token.
     *
     * @param url {String}
     * @param [options] {Object} fetch() init: method, headers, body, signal...
     * @return {Promise<Response>}
     */
    fetchWithTokenRetry(url, options = {}) {
        return this.fetch(url, false, options).then((res) => {
            if (res.ok || res.status !== 401) {
                return res
            }

            return Promise.resolve()
                .then(() => (typeof res.clone === "function" ? res.clone() : res))
                .then((readable) => readable.json())
                .catch(() => null)
                .then((body) => {
                    if (get(body, "code") !== "auth/id-token-expired") {
                        return res
                    }

                    return forceRefreshUserToken().then((token) => {
                        if (!token) {
                            return res
                        }

                        return this.fetch(url, false, {
                            ...options,
                            headers: {
                                ...options.headers,
                                Authorization: `Bearer ${token}`,
                            },
                        })
                    })
                })
        })
    }

    // A 401 from the auth middleware means no handler ran, so replaying the
    // request once against a freshly minted token is side-effect free.
    _requestWithTokenRetry(requestOptions, isPublic) {
        // axios 0.18 (what prod ran) ignored the `signal` option - it arrived in 0.22 - so no
        // request was ever cancelled through it, and callers don't expect "canceled" errors.
        // Keep that until cancellation is turned on deliberately. (cancelToken still works.)
        const { signal, ...reqOptions } = requestOptions

        return axios(reqOptions).catch((err) => {
            handleDemoTourError(err)

            if (isPublic || !this._isExpiredTokenError(err)) {
                throw err
            }

            return forceRefreshUserToken().then((token) => {
                if (!token) {
                    throw err
                }

                return axios({
                    ...reqOptions,
                    headers: {
                        ...reqOptions.headers,
                        Authorization: `Bearer ${token}`,
                    },
                })
            })
        })
    }
}

export default new Api(API_HOST)

/**
 * @deprecated Legacy mAX integration against the retired AI service; superseded by
 * src/services/maxAssistant and src/comp/maxAssistant. Kept for reference only.
 */
export const maxApi = new Api(AI_HOST)
